// Price / math / pluralization / whatsapp helpers

export const ceil10 = (x) => Math.ceil((Number(x) || 0) * 10) / 10;

export const formatEur = (x) => {
  const n = ceil10(x);
  return n.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const formatEurRaw = (x) =>
  (Number(x) || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const calcVolume = (l, w, h) =>
  ((Number(l) || 0) * (Number(w) || 0) * (Number(h) || 0)) / 1000000;

const EXCEPTIONS = new Set(["due", "tre", "da", "di", "a", "e", "in", "con", "per"]);
// prepositions / articles: once we hit one, the rest of the phrase is a complement
// already in its correct form and must NOT be pluralized ("valigie di medie dimensioni").
const STOP_WORDS = new Set([
  "di", "del", "dello", "della", "dei", "delle", "degli", "dell",
  "da", "dal", "dalla", "con", "per", "in", "nel", "nella", "su", "sul",
  "a", "ad", "al", "alla", "ai", "alle", "agli", "e", "ed", "tra", "fra", "o", "od",
]);

const pluralWord = (w) => {
  if (!w) return w;
  if (/\d/.test(w)) return w; // numbers
  if (w.length <= 2) return w;
  if (EXCEPTIONS.has(w.toLowerCase())) return w;
  if (w === w.toUpperCase() && w.length <= 4) return w; // sigle/brand brevi (TV, PC, PVC, IKEA)
  const lower = w.toLowerCase();
  // already plural: ends in "i" (masc.) or "ie" (fem. like valigie/sedie/camicie)
  if (lower.endsWith("i") || lower.endsWith("ie")) return w;
  // words ending in "e" are ambiguous (plural "scatole/valigie" vs singular "chiave"),
  // so leave them unchanged to avoid wrong forms like "valigii"/"scatoli".
  if (lower.endsWith("e")) return w;
  // -io -> -i (armadio -> armadi)
  if (lower.endsWith("io")) return w.slice(0, -2) + (w[w.length - 1] === "O" ? "I" : "i");
  const last = w[w.length - 1];
  const base = w.slice(0, -1);
  if (last === "a") return base + "e";
  if (last === "o") return base + "i";
  if (last === "A") return base + "E";
  if (last === "O") return base + "I";
  return w;
};

export const pluralize = (name, qty) => {
  if (qty <= 1) return name;
  const parts = String(name).split(/(\s+|\/)/);
  let stopped = false;
  return parts
    .map((part) => {
      if (/^\s+$/.test(part) || part === "/") return part;
      if (stopped) return part;
      if (STOP_WORDS.has(part.toLowerCase())) { stopped = true; return part; }
      return pluralWord(part);
    })
    .join("");
};

// discounts: array of all tiers; label match
export const tierPrice = (label, qty, allDiscounts) => {
  const tiers = (allDiscounts || []).filter((d) => d.catalog_item_label === label);
  for (const t of tiers) {
    const okMin = qty >= t.min_qty;
    const okMax = t.max_qty == null || qty <= t.max_qty;
    if (okMin && okMax) return t.price_per_unit;
  }
  return null;
};

export const effectivePrice = (item, allDiscounts) => {
  if (item.isPriceOverridden) return item.pricePerMonth;
  const tp = tierPrice(item.tipo, item.qty, allDiscounts);
  if (tp != null) return tp;
  return item.pricePerMonth;
};

// Build the WhatsApp message text (with *bold* markers)
const fmtDim = (n) => {
  const v = Number(n) || 0;
  return Number.isInteger(v) ? String(v) : String(v).replace(".", ",");
};
export const DEFAULT_RITIRO_DISCLAIMER = "(stima valida se il furgone può sostare entro 20 metri dall'ingresso, se tutti gli oggetti passano liberamente da porte, corridoi, ascensore e passaggi fino al furgone, e senza necessità di attrezzature speciali)";

export const DEFAULT_DURATION_DISCOUNTS = [{ months: 12, percent: 30 }, { months: 8, percent: 20 }, { months: 4, percent: 10 }];

export const parseDurationDiscounts = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (!raw) return DEFAULT_DURATION_DISCOUNTS;
  try {
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : DEFAULT_DURATION_DISCOUNTS;
  } catch {
    return DEFAULT_DURATION_DISCOUNTS;
  }
};

export const DEFAULT_GAUGE_PERIODS = [
  { months: 1, redMax: 45, yellowMax: 58, max: 80 },
  { months: 4, redMax: 45, yellowMax: 58, max: 80 },
  { months: 8, redMax: 45, yellowMax: 58, max: 80 },
  { months: 12, redMax: 45, yellowMax: 58, max: 80 },
];

export const parseGaugePeriods = (raw) => {
  const norm = (a) => a.map((p) => ({
    months: Number(p.months) || 0,
    redMax: Number(p.redMax) || 0,
    yellowMax: Number(p.yellowMax) || 0,
    max: Number(p.max) || 0,
  })).filter((p) => p.months > 0);
  if (Array.isArray(raw)) { const n = norm(raw); return n.length ? n : DEFAULT_GAUGE_PERIODS; }
  if (!raw) return DEFAULT_GAUGE_PERIODS;
  try {
    const arr = JSON.parse(raw);
    if (Array.isArray(arr)) { const n = norm(arr); return n.length ? n : DEFAULT_GAUGE_PERIODS; }
    return DEFAULT_GAUGE_PERIODS;
  } catch {
    return DEFAULT_GAUGE_PERIODS;
  }
};

// € formatters for the sales message
export const euroDec = (x) => {
  const n = ceil10(x);
  return "€" + (Number.isInteger(n)
    ? n.toLocaleString("it-IT")
    : n.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
};
export const euroRound = (x) => "€" + Math.round(Number(x) || 0).toLocaleString("it-IT");

// Free home-pickup periods (months). Supports new `freePeriods` array and legacy single `freePeriod`.
export const freeRitiroPeriods = (ritiro) => {
  if (!ritiro || !ritiro.enabled || !ritiro.freeEnabled) return [];
  if (Array.isArray(ritiro.freePeriods)) return ritiro.freePeriods.map(Number).filter((n) => n > 0);
  if (ritiro.freePeriod != null && ritiro.freePeriod !== "") return [Number(ritiro.freePeriod)];
  return [];
};

export const buildWhatsAppMessage = ({ mode, items, areaData, monthlyTotal, ritiro, ritiroTotal, allDiscounts, durationDiscounts, smontaggio, smontaggioTotal, imballaggio, imballaggioTotal, texts, abitiBox, abitiPrice, freeMaterials, adaptedWhy }) => {
  const t = texts || {};
  const L = [];
  const durDiscs = (parseDurationDiscounts(durationDiscounts) || [])
    .filter((d) => Number(d.months) > 0 && Number(d.percent) > 0)
    .sort((a, b) => Number(b.months) - Number(a.months));

  // Greeting + intro
  if (t.msgGreeting) L.push(t.msgGreeting);
  if (mode === "area_done" && t.msgIntroArea) L.push(t.msgIntroArea);
  else if (t.msgIntro) L.push(t.msgIntro);

  // Items
  if (mode === "area_done" && areaData && areaData.area) {
    const volNetto = (areaData.area * areaData.altezza * (areaData.riempimento / 100)).toFixed(1).replace(".", ",");
    L.push("*Deposito per volume*");
    L.push(`Volume netto: *${volNetto} m3*`);
    if (t.msgAreaDisclaimer) L.push(`_${t.msgAreaDisclaimer}_`);
  } else {
    items.forEach((it) => {
      const price = effectivePrice(it, allDiscounts);
      const name = pluralize(it.tipo, it.qty);
      const d = it.dims || {};
      const dimTxt = (d.l > 0 && d.w > 0 && d.h > 0) ? ` (${fmtDim(d.l)}×${fmtDim(d.w)}×${fmtDim(d.h)} cm)` : "";
      L.push(`*${it.qty} ${name}*${dimTxt}: ${euroDec(price)} x ${it.qty} = ${euroDec(price * it.qty)}/mese`);
    });
  }

  // Discounts
  L.push("*Sconti per periodi più lunghi:*");
  const freeMonths = freeRitiroPeriods(ritiro);
  durDiscs.forEach((d) => {
    const m = Number(d.months), p = Number(d.percent);
    const freeTag = freeMonths.includes(m) ? " + 🆓 Ritiro a domicilio gratuito" : "";
    L.push(`• ${m} mesi: -${p}% → ${euroRound(monthlyTotal * (1 - p / 100))}/mese${freeTag}`);
  });
  L.push(`• Costo mensile: ${euroRound(monthlyTotal)}/mese`);

  // Duration note (italic)
  if (t.msgDurationNote) L.push(`_${t.msgDurationNote}_`);

  // Ritiro
  if (ritiro && ritiro.enabled && ritiro.cap) {
    const loc = [ritiro.citta, ritiro.cap].filter(Boolean).join(", ");
    L.push(`*Ritiro a domicilio* (${loc}): *${euroRound(ritiroTotal)}*`);
    const disc = (ritiro.disclaimer != null ? ritiro.disclaimer : DEFAULT_RITIRO_DISCLAIMER).trim();
    if (disc) L.push(`_${disc}_`);
  }
  if (smontaggio && smontaggio.enabled && smontaggioTotal > 0) {
    L.push(`*Smontaggio*: *${euroRound(smontaggioTotal)}*`);
    const disc = (smontaggio.disclaimer || "").trim();
    if (disc) L.push(`_${disc}_`);
  }
  if (imballaggio && imballaggio.enabled && imballaggioTotal > 0) {
    L.push(`*Imballaggio*: *${euroRound(imballaggioTotal)}*`);
    const disc = (imballaggio.disclaimer || "").trim();
    if (disc) L.push(`_${disc}_`);
  }

  // Alternativa
  if (t.msgAlternativa) L.push(`*${t.msgAlternativa}*`);

  // Info block
  L.push("");
  if (abitiBox) {
    const ap = Number(abitiPrice) || 0;
    L.push("👗 Scatola porta abiti dedicata");
    if (t.msgAbitiDesc) L.push(t.msgAbitiDesc);
    L.push("*Prezzo scatola porta abiti*:");
    durDiscs.forEach((d) => {
      const m = Number(d.months), p = Number(d.percent);
      L.push(`• ${m} mesi: -${p}% → ${euroRound(ap * (1 - p / 100))}/mese cadauna`);
    });
    L.push(`• Costo mensile: ${euroRound(ap)}/mese cadauna`);
    if (t.msgAbitiClose) L.push(t.msgAbitiClose);
  }
  if (freeMaterials && t.msgConsegnaMateriali) {
    L.push("🎁 *Materiali d'imballaggio gratuiti*");
    L.push(t.msgConsegnaMateriali);
  }
  if (t.msgWhy) {
    const whyText = (adaptedWhy && adaptedWhy.trim()) ? adaptedWhy : t.msgWhy;
    L.push("*Perché scegliere StorMi:*");
    (whyText || "").split("\n").forEach((b) => { if (b.trim()) L.push(b); });
  }

  return L.join("\n");
};

export const stripBold = (txt) => txt.replace(/\*/g, "");
export const waMarkupToHtml = (text) => {
  const esc = (s) => String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return esc(text)
    .replace(/\*([^*\n]+)\*/g, "<b>$1</b>")
    .replace(/_([^_\n]+)_/g, "<i>$1</i>")
    .replace(/~([^~\n]+)~/g, "<s>$1</s>")
    .replace(/\n/g, "<br>");
};

export const copyText = async (text, html) => {
  // Preferred: write both plain (with WhatsApp *_ markers) and rich HTML so the
  // formatting survives when pasted into WhatsApp or any rich editor.
  try {
    if (navigator.clipboard && window.isSecureContext) {
      if (html != null && typeof window.ClipboardItem !== "undefined") {
        const item = new window.ClipboardItem({
          "text/plain": new Blob([text], { type: "text/plain" }),
          "text/html": new Blob([html], { type: "text/html" }),
        });
        await navigator.clipboard.write([item]);
        return true;
      }
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* fall through */ }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "-9999px";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    ta.setSelectionRange(0, text.length);
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
};

export const uid = () =>
  (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random().toString(16).slice(2));

// Build a labelled list of all searchable text fields of a saved quote
export const quoteSearchEntries = (q) => {
  const entries = [];
  const push = (label, text) => { if (text != null && String(text).trim()) entries.push({ label, text: String(text) }); };
  push("Numero", q.number ? `Stima #${q.number}` : "");
  push("Cliente", q.cliente_nome);
  // objects first so matches surface with the "Oggetto" label
  try { JSON.parse(q.items_json || "[]").forEach((it) => { push("Oggetto", it.tipo); push("Nota oggetto", it.note); }); } catch {}
  push("CAP", q.cliente_cap);
  push("Città", q.cliente_citta);
  push("Creato da", q.created_by);
  push("Note", q.note);
  push("Testo inserito", q.input_text);
  try {
    const r = JSON.parse(q.ritiro_json || "{}");
    push("Ritiro", r.citta); push("Ritiro CAP", r.cap); push("Disclaimer ritiro", r.disclaimer);
    if (r.smontaggio) push("Nota smontaggio", r.smontaggio.disclaimer);
    if (r.imballaggio) push("Nota imballaggio", r.imballaggio.disclaimer);
  } catch {}
  try { JSON.parse(q.market_data_json || "[]").forEach((c) => { push("Self-storage", c.name); push("Nota mercato", c.notes); }); } catch {}
  try {
    const am = JSON.parse(q.area_markets_json || "{}");
    Object.values(am).forEach((m) => (m.examples || []).forEach((e) => { push("Mercato", e.name); push("Zona", e.zona); push("Nota mercato", e.note); }));
  } catch {}
  return entries;
};

// Return {label, snippet} (snippet max 15 words) of the first field containing the query, or null
export const findQuoteSnippet = (q, query) => {
  const ql = (query || "").trim().toLowerCase();
  if (!ql) return null;
  for (const { label, text } of quoteSearchEntries(q)) {
    if (!text.toLowerCase().includes(ql)) continue;
    const words = text.split(/\s+/);
    let wi = words.findIndex((w) => w.toLowerCase().includes(ql));
    if (wi === -1) wi = 0;
    const s = Math.max(0, wi - 7);
    let snip = words.slice(s, s + 15).join(" ");
    if (s > 0) snip = "… " + snip;
    if (s + 15 < words.length) snip = snip + " …";
    return { label, snippet: snip };
  }
  return null;
};

export const quoteModeGroup = (m) => {
  if (m === "objects") return "objects";
  if (m === "area" || m === "area_done") return "area";
  if (m === "text" || m === "text_done") return "text";
  if (m === "image" || m === "image_done") return "image";
  return "other";
};

export const parseRange = (str) => {
  if (!str) return [0, 0];
  const parts = String(str).split("-").map((s) => parseFloat(s.trim()) || 0);
  if (parts.length === 1) return [parts[0], parts[0]];
  return [parts[0], parts[1]];
};

export const formatDateIt = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
};

export const formatDateTimeIt = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};
